import {
  ActivityTimeline,
  ActivityInteractionTimelineEvent,
} from '../../../models/activity-timeline';
import {
  AppointmentTimelineEvent,
  RelatedEntry,
  TaskTimelineEvent,
} from '../../../models';
import { ConfigurationMapper } from './configuration';
import { endOfDay, isAfter, startOfDay } from 'date-fns';
import { DefaultInteractions, KeyAndDisplay } from '../models';
import { ActivityTimeline as OctopusActivityTimeline } from '../models/activity-timeline';

export class ActivityTimelineMapper extends ConfigurationMapper<
  OctopusActivityTimeline[],
  ActivityTimeline[]
> {
  from(source: OctopusActivityTimeline[]): ActivityTimeline[] {
    return source.map((item) => {
      return this.mapItem(item);
    });
  }

  mapItem(source: OctopusActivityTimeline): ActivityTimeline {
    const withs = this.mapWiths(source);

    let event: ActivityTimeline = {
      key: source.Key ?? '',
      type: 'interaction',
      subject: source['$LEFT(Subject, 200)'],
      description: source['$LEFT(Description, 200)']
        ? source['$LEFT(Description, 200)'].trim()
        : undefined,
      isRecurring: !!source.RecurrenceKey,
      isWorkflowActivity: !!source.WorkflowInstanceKey,
      with: withs?.length ? withs[0] : undefined,
      withs: withs,
      regarding: this.mapRegarding(source),
      date: {
        value: source.StartDate.Value,
        display: source.StartDate.DisplayValue,
      },
      users: this.mapUsers(source),
      private: !source.SecStatus.CanRead,
      secStatus: source?.SecStatus,
      creator: {
        display: source.Creator?.DisplayValue,
        value: source.Creator?.Value,
      },
      category: !source.InteractionCategory
        ? ''
        : source.InteractionCategory.DisplayValue,
    };

    event = this.mapInteraction(event, source);

    if (DefaultInteractions.isAppointment(source.Type.Key)) {
      return this.mapAppointment(event, source) as ActivityTimeline;
    }

    if (DefaultInteractions.isTask(source.Type.Key)) {
      return this.mapTask(event, source) as ActivityTimeline;
    }

    return event;
  }

  private mapInteraction(
    event: ActivityTimeline,
    source: OctopusActivityTimeline,
  ): ActivityInteractionTimelineEvent {
    return {
      ...event,
      interaction: this.mapInteractionType(source.Type),
    };
  }

  mapUsers(source: OctopusActivityTimeline): RelatedEntry[] {
    return source.Users.map((x) =>
      this.mapRelatedEntry('user', {
        Key: x.Key.Value,
        DisplayValue: x.DisplayValue,
      } as KeyAndDisplay),
    );
  }

  mapTask(
    event: ActivityTimeline,
    source: OctopusActivityTimeline,
  ): TaskTimelineEvent {
    const isDone = source.Completed;
    const hasTime = isAfter(event.date.value, startOfDay(event.date.value));
    return {
      ...event,
      type: 'task',
      isDone,
      isOverdue:
        !isDone &&
        isAfter(
          new Date(),
          hasTime ? event.date.value : endOfDay(event.date.value),
        ),
      hasTime,
      assignedTo: source.Users?.length
        ? source.Users.map(this.mapRelatedUser)
        : [],
    };
  }

  mapAppointment(
    event: ActivityTimeline,
    source: OctopusActivityTimeline,
  ): AppointmentTimelineEvent {
    const isDone = source.Completed;
    const hasTime = isAfter(event.date.value, startOfDay(event.date.value));
    return {
      ...event,
      type: 'appointment',
      isDone,
      isOverdue:
        !isDone &&
        isAfter(
          new Date(),
          hasTime ? event.date.value : endOfDay(event.date.value),
        ),
      hasTime,
      attendees: source.Users?.length
        ? source.Users.filter(
            (user) => user.Key.Value !== source.Creator?.Value,
          ).map(this.mapRelatedUser)
        : [],
    };
  }

  private mapWiths(
    source: OctopusActivityTimeline,
  ): RelatedEntry[] | undefined {
    if (source.AbEntries?.length) {
      return source.AbEntries.map((x) =>
        this.mapRelatedEntry('addressBook', x),
      );
    } else if (source.Leads?.length) {
      return source.Leads.map((x) => this.mapRelatedEntry('lead', x));
    }
    return [];
  }

  private mapRegarding(
    source: OctopusActivityTimeline,
  ): RelatedEntry | undefined {
    if (source.With?.Case) {
      return this.mapRelatedCase(source.With.Case);
    } else if (source.With?.Opportunity) {
      return this.mapRelatedOpportunity(source.With.Opportunity);
    }
    return undefined;
  }
}
